/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Table,
  Tag,
  Button,
  Modal,
  Alert,
  Dropdown,
  Empty,
  Spin,
  Select,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  MoreOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { reservationsApi, paymentsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";

const { Option } = Select;

const STATUS_COLORS = {
  pending: "default",
  confirmed: "green",
  cancelled: "red",
  refunded: "orange",
  checked_in: "volcano",
};

const STATUS_LABELS = {
  pending: "Awaiting Payment",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  refunded: "Refunded",
  checked_in: "Checked In",
};

// ─────────────────────────────────────────────────────────────────────────────
const MyReservations = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [actionLoading, setActionLoading] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions
  const canViewOwnReservations = hasPermission(
    userPermissions,
    "reservations.view",
  );
  const canCreateReservations = hasPermission(
    userPermissions,
    "reservations.create",
  );
  const canEditReservations = hasPermission(
    userPermissions,
    "reservations.edit",
  );
  const canCancelOwnReservations = hasPermission(
    userPermissions,
    "reservations.edit",
  ); // Cancel requires edit permission
  const canMakePayments = hasPermission(userPermissions, "reservations.create"); // Payment requires create permission

  // Cancel modal
  const [cancelModal, setCancelModal] = useState({
    visible: false,
    id: null,
    reservation: null,
  });

  // Pay Now modal
  const [payModal, setPayModal] = useState({
    visible: false,
    reservation: null,
  });
  const [payMethod, setPayMethod] = useState("card");
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    if (canViewOwnReservations) {
      fetchMyReservations();
    }
  }, [filters, canViewOwnReservations]);

  const fetchMyReservations = async () => {
    try {
      setLoading(true);
      const res = await reservationsApi.getMy({
        page: filters.page,
        limit: filters.limit,
      });
      console.log("My Reservations:", res);
      setReservations(res.data || []);
      setTotal(res.total || (res.data || []).length);
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("You don't have permission to view your reservations");
      } else {
        message.error("Failed to load your reservations");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (!canCancelOwnReservations) {
      message.error("You don't have permission to cancel reservations");
      return;
    }

    try {
      setActionLoading(true);
      await reservationsApi.cancel(cancelModal.id);
      message.success("Reservation cancelled");
      setCancelModal({ visible: false, id: null, reservation: null });
      fetchMyReservations();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to cancel this reservation");
      } else {
        message.error(
          e.response?.data?.message || "Failed to cancel reservation",
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ── Pay Now ───────────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!canMakePayments) {
      message.error("You don't have permission to make payments");
      return;
    }

    const { reservation } = payModal;
    try {
      setPayLoading(true);
      const result = await paymentsApi.initiate({
        reservationId: reservation.id,
        method: payMethod,
      });
      // Customers use the public /confirm endpoint — simulateSuccess is admin-only
      await paymentsApi.confirmPayment(result.paymentId);
      message.success("Payment successful! Your ticket has been issued.");
      setPayModal({ visible: false, reservation: null });
      fetchMyReservations();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to make payments");
      } else {
        message.error(
          e.response?.data?.message || "Payment failed. Please try again.",
        );
      }
    } finally {
      setPayLoading(false);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Trip",
      key: "trip",
      render: (_, r) => (
        <div>
          <strong>{r.tripDate?.trip?.name || "—"}</strong>
          <div className="small text-muted">{r.tripDate?.date || "—"}</div>
        </div>
      ),
    },
    {
      title: "Time",
      key: "time",
      render: (_, r) => {
        const t = r.tripDate?.trip;
        return t ? `${t.startTime} – ${t.endTime}` : "—";
      },
    },
    { title: "Deck", key: "deck", render: (_, r) => r.deck?.name || "—" },
    { title: "Seats", dataIndex: "seats", key: "seats", align: "center" },
    {
      title: "Total",
      dataIndex: "totalPrice",
      key: "total",
      render: (v) => `EGP ${Number(v).toLocaleString()}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag
          color={STATUS_COLORS[s] || "default"}
          icon={
            s === "pending" ? (
              <ClockCircleOutlined />
            ) : s === "confirmed" || s === "checked_in" ? (
              <CheckCircleOutlined />
            ) : (
              undefined
            )
          }
        >
          {STATUS_LABELS[s] || s?.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Ticket",
      key: "ticket",
      render: (_, r) => {
        // Pending = not paid yet, no ticket should show
        if (r.status === "pending")
          return <span style={{ color: "#d1d5db" }}>—</span>;
        if (!r.ticket) return <span style={{ color: "#d1d5db" }}>—</span>;
        if (
          r.ticket.isCancelled ||
          r.status === "cancelled" ||
          r.status === "refunded"
        ) {
          return (
            <code
              style={{
                background: "#f3f4f6",
                padding: "2px 8px",
                borderRadius: 4,
                color: "#9ca3af",
                fontSize: 12,
                textDecoration: "line-through",
              }}
            >
              {r.ticket.ticketNumber}
            </code>
          );
        }
        return (
          <code
            style={{
              background: "#f0f4ff",
              padding: "2px 8px",
              borderRadius: 4,
              color: "#7B4D12",
              fontSize: 12,
            }}
          >
            {r.ticket.ticketNumber}
          </code>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, r) => {
        const items = [
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => history.push(`/reservations/${r.id}`),
          },
        ];

        // Pay Now — for pending unpaid reservations (requires payment permission)
        if (r.status === "pending" && canMakePayments) {
          items.push({ type: "divider" });
          items.push({
            key: "pay",
            label: "Pay Now",
            icon: <CreditCardOutlined />,
            onClick: () => {
              setPayMethod("card");
              setPayModal({ visible: true, reservation: r });
            },
          });
          items.push({ type: "divider" });
          items.push({
            key: "cancel",
            label: "Cancel",
            icon: <CloseCircleOutlined />,
            danger: true,
            onClick: () =>
              setCancelModal({ visible: true, id: r.id, reservation: r }),
          });
        }

        // Cancel — for confirmed reservations only (requires edit permission)
        if (r.status === "confirmed" && canCancelOwnReservations) {
          items.push({ type: "divider" });
          items.push({
            key: "cancel",
            label: "Cancel",
            icon: <CloseCircleOutlined />,
            danger: true,
            onClick: () =>
              setCancelModal({ visible: true, id: r.id, reservation: r }),
          });
        }

        // If no actions available besides view, show view button directly
        if (items.length === 1) {
          return (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => history.push(`/reservations/${r.id}`)}
            />
          );
        }

        return (
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Permission check for viewing the page
  if (!canViewOwnReservations) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view your reservations.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> reservations.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0 premium-card">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>My Reservations</h5>
                  {canCreateReservations && (
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/reservations/add")}
                    >
                      Book a Trip
                    </Button>
                  )}
                </div>
              </div>

              {/* Show view-only mode message if user has view but not edit/create permissions */}
              {!canEditReservations && !canCreateReservations && (
                <Alert
                  message="View Only Mode"
                  description="You can view your reservations but cannot make changes or create new bookings."
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )}

              {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <Spin size="large" />
                </div>
              ) : reservations.length === 0 ? (
                <Empty
                  description="You have no reservations yet"
                  style={{ padding: "60px 0" }}
                >
                  {canCreateReservations && (
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/reservations/add")}
                    >
                      Book Your First Trip
                    </Button>
                  )}
                </Empty>
              ) : (
                <Table
                  columns={columns}
                  dataSource={reservations}
                  rowKey="id"
                  loading={loading}
                  rowClassName={(r) =>
                    r.status === "cancelled" || r.status === "refunded"
                      ? "opacity-50"
                      : ""
                  }
                  pagination={{
                    current: filters.page,
                    pageSize: filters.limit,
                    total,
                    showSizeChanger: true,
                    showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} reservations`,
                  }}
                  onChange={(p) =>
                    setFilters((f) => ({
                      ...f,
                      page: p.current,
                      limit: p.pageSize,
                    }))
                  }
                  scroll={{ x: true }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal
        title="Cancel Reservation"
        open={cancelModal.visible}
        onOk={handleCancel}
        onCancel={() =>
          setCancelModal({ visible: false, id: null, reservation: null })
        }
        confirmLoading={actionLoading}
        okText="Yes, Cancel It"
        okType="danger"
      >
        {cancelModal.reservation?.status === "confirmed" &&
        cancelModal.reservation?.ticket ? (
          // Paid reservation — show refund details
          <>
            <Alert
              message="Your money will be refunded"
              description={
                <span>
                  A refund of{" "}
                  <strong style={{ color: "#16a34a" }}>
                    EGP{" "}
                    {Number(
                      cancelModal.reservation?.totalPrice || 0,
                    ).toLocaleString()}
                  </strong>{" "}
                  will be processed to your original payment method within 5–7
                  business days. A confirmation email will be sent to you.
                </span>
              }
              type="success"
              showIcon
              className="mb-3"
            />
            <div
              style={{
                background: "#f0fdf4",
                border: "2px solid #86efac",
                borderRadius: 10,
                padding: "14px 18px",
                marginBottom: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "#15803d", fontWeight: 600 }}>
                💳 Refund Amount
              </span>
              <span style={{ fontSize: 20, fontWeight: 800, color: "#16a34a" }}>
                EGP{" "}
                {Number(
                  cancelModal.reservation?.totalPrice || 0,
                ).toLocaleString()}
              </span>
            </div>
          </>
        ) : (
          // Pending / unpaid — no refund needed
          <Alert
            message="Cancel this reservation?"
            description="No payment was made for this reservation so no refund is needed."
            type="warning"
            showIcon
            className="mb-3"
          />
        )}
        <p style={{ color: "#6b7280", fontSize: 13 }}>
          Are you sure you want to cancel? This action cannot be undone.
        </p>
      </Modal>

      {/* Pay Now Modal - Only show if user can make payments */}
      {canMakePayments && (
        <Modal
          title={
            <span>
              <CreditCardOutlined style={{ marginRight: 8 }} />
              Complete Payment
            </span>
          }
          open={payModal.visible}
          onOk={handlePay}
          onCancel={() => setPayModal({ visible: false, reservation: null })}
          confirmLoading={payLoading}
          okText={`Pay EGP ${Number(
            payModal.reservation?.totalPrice || 0,
          ).toLocaleString()}`}
          width={440}
          destroyOnClose
        >
          <Alert
            message="Complete payment to confirm your booking and receive your ticket."
            type="info"
            showIcon
            className="mb-4"
          />

          <div className="input-block mb-3">
            <label
              style={{
                fontWeight: 600,
                fontSize: 13,
                display: "block",
                marginBottom: 8,
              }}
            >
              Payment Method
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { value: "card", label: "Credit / Debit Card", icon: "💳" },
                { value: "fawry", label: "Fawry", icon: "🟠" },
                { value: "paymob", label: "Paymob", icon: "📱" },
                { value: "cash", label: "Cash on Boarding", icon: "💵" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "11px 16px",
                    border: `2px solid ${
                      payMethod === opt.value ? "#7B4D12" : "#e5e7eb"
                    }`,
                    borderRadius: 10,
                    cursor: "pointer",
                    background: payMethod === opt.value ? "#F5ECD7" : "#fff",
                    transition: "all .15s",
                    userSelect: "none",
                  }}
                  onClick={() => setPayMethod(opt.value)}
                >
                  <input
                    type="radio"
                    name="myResPayMethod"
                    checked={payMethod === opt.value}
                    onChange={() => setPayMethod(opt.value)}
                    style={{ accentColor: "#7B4D12", width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: 18 }}>{opt.icon}</span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: payMethod === opt.value ? 600 : 400,
                      color: payMethod === opt.value ? "#7B4D12" : "#374151",
                    }}
                  >
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <Divider style={{ margin: "16px 0 12px" }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
            }}
          >
            <span style={{ color: "#6b7280" }}>Total</span>
            <span style={{ color: "#7B4D12", fontSize: 18 }}>
              EGP{" "}
              {Number(payModal.reservation?.totalPrice || 0).toLocaleString()}
            </span>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MyReservations;
